import React, { useState, useEffect, createContext, useContext } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import { Toaster } from '@/components/ui/sonner';
import { toast } from 'sonner';
import '@/App.css';

// Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Configure axios
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Pages
import HomePage from '@/pages/HomePage';
import ListingDetailPage from '@/pages/ListingDetailPage';
import CreateListingPage from '@/pages/CreateListingPage';
import EditListingPage from '@/pages/EditListingPage';
import MyBidsPage from '@/pages/MyBidsPage';
import MyListingsPage from '@/pages/MyListingsPage';
import TradesPage from '@/pages/TradesPage';
import ProfilePage from '@/pages/ProfilePage';
import AuthPage from '@/pages/AuthPage';
import ForgotPasswordPage from '@/pages/ForgotPasswordPage';
import MessagesPage from '@/pages/MessagesPage';
import ChatPage from '@/pages/ChatPage';
import FAQPage from '@/pages/FAQPage';
import Navbar from '@/components/Navbar';
import CategoryChipBar from '@/components/CategoryChipBar';

// Admin Pages
import AdminLayout from '@/pages/admin/AdminLayout';
import AdminDashboard from '@/pages/admin/AdminDashboard';
import AdminUsers from '@/pages/admin/AdminUsers';
import AdminListings from '@/pages/admin/AdminListings';
import AdminBids from '@/pages/admin/AdminBids';
import AdminAnalytics from '@/pages/admin/AdminAnalytics';
import AdminSettings from '@/pages/admin/AdminSettings';
import AdminLogin from '@/pages/admin/AdminLogin';
import AdminAds from '@/pages/admin/AdminAds';
import AdminCreateListings from '@/pages/admin/AdminCreateListings';
import AdminCategories from '@/pages/admin/AdminCategories';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user } = useAuth();
  if (!user) {
    toast.error('Please login to access this feature');
    return <Navigate to="/auth" />;
  }
  return children;
};

// Anonymous Visit Tracker (one record per browser session)
const VisitTracker = () => {
  useEffect(() => {
    try {
      const KEY = 'b2b_visit_session';
      let sessionId = sessionStorage.getItem(KEY);
      const isNewSession = !sessionId;
      if (!sessionId) {
        sessionId = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
        sessionStorage.setItem(KEY, sessionId);
      }
      // Only track once per session to avoid spam
      if (isNewSession) {
        axios.post(`${API_URL}/api/visits/track`, {
          session_id: sessionId,
          path: window.location.pathname || '/',
          referrer: document.referrer || '',
          user_agent: navigator.userAgent || '',
        }).catch((err) => {
          console.debug('Visit tracking failed:', err?.message);
        });
      }
    } catch (err) {
      console.debug('Visit tracker init failed:', err?.message);
    }
  }, []);
  return null;
};

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      fetchCurrentUser();
    } else {
      setLoading(false);
    }
  }, []);

  const fetchCurrentUser = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/auth/me`);
      setUser(response.data);
      localStorage.setItem('user', JSON.stringify(response.data));
    } catch (error) {
      console.error('Failed to fetch user', error);
      localStorage.removeItem('token');
      localStorage.removeItem('user');
    } finally {
      setLoading(false);
    }
  };

  const login = (token, userData) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    toast.success('Welcome to Bid2Barter!');
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    toast.success('Logged out successfully');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ user, login, logout, fetchCurrentUser }}>
      <BrowserRouter>
        <div className="min-h-screen bg-slate-50">
          <VisitTracker />
          <Navbar />
          <CategoryChipBar />
          <Routes>
            {/* Public Routes - Anyone can access */}
            <Route path="/auth" element={!user ? <AuthPage /> : <Navigate to="/" />} />
            <Route path="/forgot-password" element={!user ? <ForgotPasswordPage /> : <Navigate to="/" />} />
            <Route path="/" element={<HomePage />} />
            <Route path="/listing/:id" element={<ListingDetailPage />} />
            <Route path="/profile/:userId" element={<ProfilePage />} />
            <Route path="/faq" element={<FAQPage />} />
            
            {/* Protected Routes - Login required for trading */}
            <Route path="/create-listing" element={<ProtectedRoute><CreateListingPage /></ProtectedRoute>} />
            <Route path="/edit-listing/:id" element={<ProtectedRoute><EditListingPage /></ProtectedRoute>} />
            <Route path="/my-bids" element={<ProtectedRoute><MyBidsPage /></ProtectedRoute>} />
            <Route path="/my-listings" element={<ProtectedRoute><MyListingsPage /></ProtectedRoute>} />
            <Route path="/trades" element={<ProtectedRoute><TradesPage /></ProtectedRoute>} />
            <Route path="/messages" element={<ProtectedRoute><MessagesPage /></ProtectedRoute>} />
            <Route path="/chat/:bidId" element={<ProtectedRoute><ChatPage /></ProtectedRoute>} />
            
            {/* Admin Routes */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={<AdminLayout />}>
              <Route index element={<AdminDashboard />} />
              <Route path="users" element={<AdminUsers />} />
              <Route path="listings" element={<AdminListings />} />
              <Route path="create-listings" element={<AdminCreateListings />} />
              <Route path="bids" element={<AdminBids />} />
              <Route path="ads" element={<AdminAds />} />
              <Route path="categories" element={<AdminCategories />} />
              <Route path="analytics" element={<AdminAnalytics />} />
              <Route path="settings" element={<AdminSettings />} />
            </Route>
          </Routes>
          <Toaster position="top-center" richColors />
        </div>
      </BrowserRouter>
    </AuthContext.Provider>
  );
}

export default App;
